import { doc, collection, runTransaction } from 'firebase/firestore';
import { db } from '../../config';
import type { OcrCreditUsageType } from '../../../../types/ocrCreditUsage';
import { usageType } from '../../../../constants/enum';

const companiesRef = collection(db, 'companies');

/**
 * OCRクレジットを追加し、履歴を保存する
 * トランザクションで企業のocr_credit_balanceと履歴を同時に更新
 * 履歴はcompanies/{companyId}/ocr_credit_usageサブコレクションに保存
 * @param companyId - 対象企業ID
 * @param amount - 追加クレジット額
 * @param note - 備考
 * @param createdBy - 作成者UID（運営管理者）
 */
export const addOcrCredit = async (
  companyId: string,
  amount: number,
  note: string,
  createdBy: string,
): Promise<void> => {
  const companyDocRef = doc(companiesRef, companyId);
  // サブコレクション: companies/{companyId}/ocr_credit_usage
  const usageCollectionRef = collection(companyDocRef, 'ocr_credit_usage');
  const usageDocRef = doc(usageCollectionRef);
  const now = new Date();

  await runTransaction(db, async transaction => {
    // トランザクション内で企業ドキュメントを取得し、最新の残高を使用
    const companyDoc = await transaction.get(companyDocRef);
    if (!companyDoc.exists()) {
      throw new Error('Company not found');
    }

    const companyData = companyDoc.data();
    const beforeBalance = (companyData.ocr_credit_balance as number) ?? 0;
    const afterBalance = beforeBalance + amount;

    // 企業のocr_credit_balanceを更新
    transaction.update(companyDocRef, {
      ocr_credit_balance: afterBalance,
      updated_at: now,
      updated_by: createdBy,
    });

    // 有効期限を計算（付与日から1年後）
    const expiresAt = new Date(now);
    expiresAt.setFullYear(expiresAt.getFullYear() + 1);

    // 利用履歴をサブコレクションに追加
    const usageData: Omit<OcrCreditUsageType, 'id'> = {
      company_id: companyId,
      usage_type: usageType.manualGrant,
      amount,
      before_balance: beforeBalance,
      after_balance: afterBalance,
      note,
      created_at: now,
      created_by: createdBy,
      expires_at: expiresAt,
      remaining_amount: beforeBalance < 0 ? Math.max(0, amount + beforeBalance) : amount,
    };
    transaction.set(usageDocRef, usageData);
  });
};
